import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AssignVehiclesRequestBody,
  Vehicle,
  VehicleCreate, VehiclePagedList,
  VehicleUpdate,
} from 'src/swagger-gen/web-secured/models';
import { BaseService } from '../services/base.service';
import { AuthService } from '../tinea-components/okta/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class VehicleRepo extends BaseService {
  vehiclesUrl = environment.apiUrl + '/vehicles';
  subscriptionUrl = environment.apiUrl + '/subscriptions';
  readonly VEHICLES_LIMIT = '50';
  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    public authService: AuthService
  ) {
    super();
  }

  /**
   * Get Vehicles List
   * @param offset offset
   * @param limit limit
   * @param params params
   * @param body body
   * @returns Observable vehicles list
   */
  getVehicles(
    offset: number,
    limit: number,
    params: any[]
  ): Observable<VehiclePagedList> {
    let requestString = this.pagination(offset, limit, this.VEHICLES_LIMIT);

    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });

      requestString += queryParams;
    }

    const requestParams = new HttpParams({ fromString: requestString });

    const httpOptions = {
      headers: this.httpHeaders(),
      params: requestParams,
    };

    return this.http
      .post<VehiclePagedList>(this.vehiclesUrl + '/search', null, httpOptions)
      .pipe(
        tap((vehicles: VehiclePagedList) => {
          this.logger.log(`Got ${vehicles.paging.totalItems} vehicles.`); // DEBUG
        }),
        catchError(this.handleServerError)
      );
  }


  /**
   * Get Vehicle
   * @param uid uid of vehicle
   * @returns Observable vehicle
   */
  getVehicle(uid: string): Observable<Vehicle> {
    const httpOptions = {
      headers: this.httpHeaders(),
    };

    return this.http
      .get<Vehicle>(this.vehiclesUrl + '/' + uid, httpOptions)
      .pipe(
        tap((vehicle: Vehicle) => {
          this.logger.log(`Got ${vehicle.uid} vehicle.`); // DEBUG
        }),
        catchError(this.handleServerError)
      );
  }
  /**
   * Create Vehicle
   * @param vehicle vehicle Object
   */
  createVehicle(vehicle: VehicleCreate) {
    const httpOptions: any = {
      headers: this.httpHeaders(),
      observe: 'response'
    };

    return this.http.post<any>(this.vehiclesUrl, vehicle, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Got vehicleCreate response.`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Create Vehicle
   * @param uid string
   * @param vehicle vehicle Object
   */
  updateVehicle(uid: string, vehicle: VehicleUpdate) {
    const httpOptions = {
      headers: this.httpHeaders(),
    };

    return this.http.put<any>(this.vehiclesUrl + '/' + uid, vehicle, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }

  /**
   * Subscribe vehicle to default plan
   * @param uid user uid
   */
   subscribeVehicleToDefaultPlan(uid: string): Observable<any> {
    const url = `${this.vehiclesUrl}/${uid}/subscribe`;
    const httpOptions: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      observe: 'response'
    };

    return this.http.post<any>(url, null, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Vehicle subscription response`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportVehicleDefinition(): Observable<any> {
    const url = `${this.vehiclesUrl}/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http.get<any>(url, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }

  importVehicleDefinition(file: any): Observable<any> {
    const url = `${this.vehiclesUrl}/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response'
    };

    return this.http.post<any>(url, file, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }


  deleteVehicle(uid: string): Observable<any> {
     const url = this.vehiclesUrl + '/' + uid;

     const httpOptions = {
       headers: new HttpHeaders({
         'Content-Type': 'application/json',
       })
     };

     return this.http.delete<any>(url, httpOptions).pipe(
       catchError(this.handleServerError)
     );
   }
}
